import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { Grid } from "@material-ui/core";
import CustomModal from "../../components/common/customModal";
import CustomInput from "../../components/common/inputField";
import LogoUpload from "../../components/common/logoUpload";
import {
  LearningDevelopmentFormDataState,
  LearningDevelopmentFormErrors,
} from "../../types/dashboard";
import { showToast } from "../../components/common/toastNotification";
import dashboardService from "../../services/DashboardServices";

interface LearningDevelopmentModalProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  learningDevelopmentUpdateData: LearningDevelopmentFormDataState;
}

const LearningDevelopmentModal: React.FC<LearningDevelopmentModalProps> = ({
  open,
  onClose,
  onSave,
  learningDevelopmentUpdateData,
}) => {
  const [formData, setFormData] = useState<LearningDevelopmentFormDataState>({
    id: null,
    title: "",
    logoUrl: "",
    logo: null,
    link: "",
  });

  const [formErrors, setFormErrors] = useState({
    title: "",
    link: "",
    logoUrl: "",
  });
  const [btnLoading, setBtnLoading] = useState(false);

  useEffect(() => {
    if (learningDevelopmentUpdateData) {
      setFormData({
        id: learningDevelopmentUpdateData.id || null,
        title: learningDevelopmentUpdateData.title,
        logoUrl: learningDevelopmentUpdateData.logoUrl || "",
        logo: learningDevelopmentUpdateData.logo || null,
        link: learningDevelopmentUpdateData.link || "",
      });
    }
  }, [learningDevelopmentUpdateData]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setFormData((prev) => ({ ...prev, logo: file }));
    if (file) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        logoUrl: "",
      }));
    }
  };

  //// handle form validation code
  const validateForm = () => {
    const errors: LearningDevelopmentFormErrors = {};
    const urlRegex =
      /^(https?:\/\/)(([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,})(\/[^\s]*)?$/i;

    if (!formData.title.trim()) {
      errors.title = "Title is required.";
    }

    if (formData.link.trim()) {
      if (!urlRegex.test(formData.link.trim())) {
        errors.link = "Enter a valid Google Drive link.";
      }
    }
    const logoUrl = formData.logoUrl?.trim() || "";
    if (!logoUrl && !formData.logo) {
      errors.logoUrl = "Either a file or a URL must be provided.";
      showToast(errors.logoUrl, "error");
    } else if (logoUrl && !urlRegex.test(logoUrl)) {
      errors.logoUrl = "Enter a valid URL for the logo.";
    }
    setFormErrors({
      title: errors.title || "",
      link: errors.link || "",
      logoUrl: errors.logoUrl || "",
    });

    return Object.keys(errors).length === 0;
  };

  const handleSaveData = async () => {
    if (!validateForm()) return;

    try {
      setBtnLoading(true);
      const { id, title, logoUrl, logo, link } = formData;

      const formDataToSend = new FormData();
      formDataToSend.append("Id", id?.toString() || "0");
      formDataToSend.append("Title", title.trim());
      formDataToSend.append("Link", link.trim());

      if (logo instanceof File) {
        formDataToSend.append("Logo", logo);
        formDataToSend.append("LogoUrl", "");
      } else if (logoUrl?.trim()) {
        formDataToSend.append("LogoUrl", logoUrl.trim());
        formDataToSend.append("Logo", "");
      }

      const numericId = id ?? 0;

      // API call based on ID
      const response =
        numericId > 0
          ? await dashboardService.updateLearningDevelopmentDataAPI(
              formDataToSend
            )
          : await dashboardService.addLearningDevelopmentDataAPI(
              formDataToSend
            );

      // Handle API Response
      if (response?.data) {
        showToast(response.data.message, "success");
        onSave();
        onClose();
      }
      setBtnLoading(false);
    } catch (error) {
      console.error("Error saving learning development data:", error);
    } finally {
      setBtnLoading(false);
    }
  };

  return (
    <CustomModal
      open={open}
      handleClose={onClose}
      title="Learning & Development"
      onSave={handleSaveData}
      btnLoading={btnLoading}
    >
      <Box mt={2}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={12}>
            <CustomInput
              label="Title"
              type="text"
              name="title"
              placeholder="Enter Title"
              value={formData.title}
              onChange={handleChange}
              error={formErrors.title}
              required
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <LogoUpload
              label="Upload Logo"
              onChange={handleFileUpload}
              fileName={formData.logo instanceof File ? formData.logo.name : ""}
            ></LogoUpload>
          </Grid>
          <Grid item xs={12} md={9}>
            <CustomInput
              label="Upload logo via the URL"
              type="text"
              name="logoUrl"
              placeholder="Enter URL"
              value={formData.logoUrl}
              onChange={handleChange}
              error={formErrors.logoUrl}
            />
          </Grid>
          <Grid item xs={12} md={12}>
            <CustomInput
              label="URL of the google drive Link"
              type="text"
              name="link"
              placeholder="Enter the URL of the google drive Link"
              value={formData.link}
              onChange={handleChange}
              error={formErrors.link}
              // required
            />
          </Grid>
        </Grid>
      </Box>
    </CustomModal>
  );
};

export default LearningDevelopmentModal;
